import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useRef, useState } from "react";
import { showNotification } from "context/notification.service";
import { libraryDocumentsApi } from "@services/api";

function formatFileSize(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${(octets / 1024).toFixed(1)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
}

export const Library = () => {
  const libraryDocuments = useProjectStore((state) => state.libraryDocuments);
  const fetchLibraryDocuments = useProjectStore((state) => state.fetchLibraryDocuments);
  const removeLibraryDocuments = useProjectStore(
    (state) => state.removeLibraryDocuments,
  );
  const userData = useDataStore((state) => state.userData);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const orgId = userData?.org_id;
    if (!orgId) {
      showNotification({ type: "error", message: "Organisation non disponible" });
      return;
    }

    setIsUploading(true);
    try {
      await Promise.all(
        Array.from(files).map((file) => libraryDocumentsApi.upload(file, orgId))
      );
      await fetchLibraryDocuments(orgId);
      showNotification({
        type: "success",
        message: `${files.length} document${files.length > 1 ? "s" : ""} ajouté${files.length > 1 ? "s" : ""}`,
      });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      showNotification({
        type: "error",
        message: detail ? `Erreur : ${detail}` : "Erreur lors de l'upload",
      });
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = (id: string) => {
    removeLibraryDocuments([id]);
    showNotification({
      type: "success",
      message: "Document supprimé",
    });
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 ml-10">
      <div className="flex flex-col w-400 gap-4">
        <div className="flex flex-col">
          <Text1>Bibliothèque</Text1>
          <Text2>Gérez vos documents de bibliothèque ici.</Text2>
          <Text3>
            Ces documents peuvent être associés à vos modèles d'emails et seront
            automatiquement joints lors de l'envoi.
          </Text3>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      <div className="flex flex-col gap-2 mt-4 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="flex flex-col divide-y">
          {libraryDocuments.length === 0 && (
            <div className="text-h2 text-neutral-400 py-4 text-center">
              Aucun document dans la bibliothèque
            </div>
          )}
          {libraryDocuments.map((doc) => (
            <div
              key={doc.id}
              className="flex flex-row items-center px-4 py-3 gap-3"
            >
              <Icon1 name="file" size={20} color="grey" />
              <div className="flex flex-col flex-1 min-w-0">
                <a
                  href={doc.file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-h2 font-medium truncate hover:text-blue-600 hover:underline cursor-pointer"
                >
                  {doc.name}
                </a>
                <div className="text-h3 text-neutral-400">
                  {formatFileSize(doc.file.octets)}
                </div>
              </div>
              <SecondaryButton
                label="Supprimer"
                variant="small"
                danger={true}
                onClick={() => handleDelete(doc.id)}
              />
            </div>
          ))}
        </div>

        <Button
          className="mt-4"
          label={isUploading ? "Envoi en cours..." : "Ajouter un document"}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        />
      </div>
    </div>
  );
};
